import * as React from "react";
import { useLocation } from "react-router";

import { TypeOmit } from "../const";

export type ScrollPositionProps = {
  saveScrollPosition: () => void;
};

const withScrollPosition =
  (option: { autoParams?: boolean; autoScroll?: boolean }) =>
  <Props extends ScrollPositionProps = ScrollPositionProps>(
    Component: React.ComponentType<Props>
  ) => {
    const Comp: React.FC<TypeOmit<Props, keyof ScrollPositionProps>> = (
      props
    ) => {
      const scrollKey: string = window.location.pathname + "@scrollKey";
      const paramsKey: string = window.location.pathname + "@paramsKey";
      const location = useLocation<{ autoScroll: boolean }>();
      restoreRouteParams();
      React.useEffect(() => restoreScrollPosition(), []);
      return (
        <Component
          {...(props as any)}
          saveScrollPosition={saveScrollPosition}
        />
      );
      function saveScrollPosition(search?: string) {
        localStorage.setItem(
          scrollKey,
          (
            document.documentElement.scrollTop ||
            window.scrollY ||
            document.body.scrollTop
          ).toString() // compatible with ios
        );
        localStorage.setItem(
          paramsKey,
          (typeof search === "string" && search) || window.location.search
        );
      }
      function restoreRouteParams() {
        if (location.state && location.state.autoScroll && option.autoParams) {
          localStorage.removeItem(paramsKey);
        }
      }
      function restoreScrollPosition() {
        if (location.state && location.state.autoScroll && option.autoScroll) {
          const scrollY = localStorage.getItem(scrollKey);
          localStorage.removeItem(scrollKey);
          if (scrollY) {
            window.scrollTo(0, parseInt(scrollY, 0));
          }
        }
      }
    };
    return Comp;
  };

type Props = {
  children: (
    options: ScrollPositionProps["saveScrollPosition"]
  ) => React.ReactNode;
};

export const AutoScrollPosition = withScrollPosition({ autoScroll: true })(
  (props: Props & ScrollPositionProps) => {
    return <>{props.children(props.saveScrollPosition)}</>;
  }
);

export const AutoUrlParams = withScrollPosition({ autoParams: true })(
  (props) => {
    return <>{props.children}</>;
  }
);

export const getUrlParams = (path: string) => {
  const paramsKey = path + "@paramsKey";
  return localStorage.getItem(paramsKey) || "";
};

// hook version
export const useScrollPosition = () => {
  const scrollKey: string = window.location.pathname + "@scrollKey";
  const paramsKey: string = window.location.pathname + "@paramsKey";
  const location = useLocation<{ autoScroll: boolean }>();
  React.useEffect(() => restoreScrollPosition(), []);
  function saveScrollPosition(search?: string) {
    localStorage.setItem(
      scrollKey,
      (
        document.documentElement.scrollTop ||
        window.scrollY ||
        document.body.scrollTop
      ).toString() // compatible with ios
    );
    localStorage.setItem(
      paramsKey,
      (typeof search === "string" && search) || window.location.search
    );
  }
  function restoreScrollPosition() {
    if (location.state && location.state.autoScroll) {
      const scrollY = localStorage.getItem(scrollKey);
      localStorage.removeItem(scrollKey);
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY, 0));
      }
    }
  }

  return { saveScrollPosition };
};

export default withScrollPosition;
